/**
 * Patrol one-off accessibility fix for Pamela Love (PAT-1387).
 *
 * Klaviyo's signup popup does not return focus to the triggering element when
 * it closes (WCAG 2.4.3). Klaviyo declined to fix it upstream, so this script,
 * loaded only on the Patrol Preview Theme, supplies the full dialog focus
 * lifecycle by keying off Klaviyo's own `klaviyoForms` window event:
 *   - open  -> capture the trigger, move focus into the popup, trap Tab
 *   - close -> restore focus to the trigger
 *
 * Self-contained: no dependency on any Patrol theme asset. Inert on any page
 * where Klaviyo never dispatches `klaviyoForms`.
 */
(function () {
  "use strict";
  var KLAVIYO_CONTAINER = '[class*="klaviyo-form-"]';
  // Broad match for "is this element Klaviyo's own chrome" — covers the form,
  // the reset-css wrapper, and the post-close reminder/teaser tab. Used both to
  // avoid capturing Klaviyo nodes as the restore target and to detect when
  // Klaviyo has stolen focus back after close.
  var KLAVIYO_ANY =
    '[class*="klaviyo"], [class*="kl-private-reset"], [id*="klaviyo"]';
  var FOCUSABLE =
    'a[href], area[href], input:not([disabled]):not([type="hidden"]), ' +
    "select:not([disabled]), textarea:not([disabled]), button:not([disabled]), " +
    'iframe, [tabindex]:not([tabindex="-1"]), [contenteditable]';
  var triggerElement = null;
  var lastExternalFocus = null;
  var activeDialog = null;
  var keydownHandler = null;
  function isInsideKlaviyo(node) {
    return !!(node && node.closest && node.closest(KLAVIYO_ANY));
  }
  // Track the last element the user focused OUTSIDE any Klaviyo form. This is
  // more reliable than reading document.activeElement at open time, because
  // Klaviyo autofocuses its first field before/while dispatching "open".
  document.addEventListener(
    "focusin",
    function (event) {
      var target = event.target;
      if (target instanceof HTMLElement && !isInsideKlaviyo(target)) {
        lastExternalFocus = target;
      }
    },
    true,
  );
  function findDialog(formId) {
    var candidates = [];
    if (formId && window.CSS && CSS.escape) {
      var byId = document.querySelector(".klaviyo-form-" + CSS.escape(formId));
      if (byId) {
        candidates.push(byId);
      }
    }
    var containers = document.querySelectorAll(KLAVIYO_CONTAINER);
    for (var i = 0; i < containers.length; i++) {
      candidates.push(containers[i]);
    }
    for (var j = 0; j < candidates.length; j++) {
      var node = candidates[j];
      if (node.getClientRects().length === 0) {
        continue;
      }
      // Klaviyo wraps the inner `klaviyo-form-*` element in a separate
      // `role="dialog"` ancestor that also holds the close button. Walk UP to
      // that wrapper so the trap and close-control lookup include the whole
      // dialog, not just the form fields. `closest` from a Klaviyo node can
      // only reach Klaviyo's own dialog, never the theme's other dialogs.
      var dialog =
        node.closest('[role="dialog"]') ||
        (node.matches('[role="dialog"]') ? node : null) ||
        node.querySelector('[role="dialog"]') ||
        node;
      if (dialog.getClientRects().length > 0) {
        return dialog;
      }
    }
    return null;
  }
  function visibleFocusables(root) {
    return Array.prototype.slice
      .call(root.querySelectorAll(FOCUSABLE))
      .filter(function (el) {
        return el.getClientRects().length > 0;
      });
  }
  function findCloseControl(root) {
    var controls = root.querySelectorAll(
      '[aria-label*="close" i], button[class*="close" i], [class*="close" i][role="button"]',
    );
    for (var i = 0; i < controls.length; i++) {
      if (controls[i].getClientRects().length > 0) {
        return controls[i];
      }
    }
    return null;
  }
  function teardownTrap() {
    if (keydownHandler) {
      document.removeEventListener("keydown", keydownHandler, true);
      keydownHandler = null;
    }
  }
  // Klaviyo fires the "open" event BEFORE it renders the popup DOM, so a single
  // rAF is too early — poll briefly until the dialog node exists.
  function waitForDialog(formId, onFound) {
    var attempts = 0;
    (function poll() {
      var dialog = findDialog(formId);
      if (dialog) {
        onFound(dialog);
        return;
      }
      if (attempts++ > 60) {
        return;
      }
      window.setTimeout(poll, 50);
    })();
  }
  function onOpen(formId) {
    triggerElement =
      lastExternalFocus &&
      lastExternalFocus.isConnected &&
      !isInsideKlaviyo(lastExternalFocus)
        ? lastExternalFocus
        : null;
    waitForDialog(formId, function (dialog) {
      activeDialog = dialog;
      // Klaviyo often moves focus into the dialog itself; only step in when it
      // has not, to avoid a focus tug-of-war.
      var active = document.activeElement;
      if (!active || !dialog.contains(active)) {
        var focusables = visibleFocusables(dialog);
        if (focusables.length > 0) {
          focusables[0].focus();
        } else {
          if (!dialog.hasAttribute("tabindex")) {
            dialog.setAttribute("tabindex", "-1");
          }
          dialog.focus();
        }
      }
      teardownTrap();
      keydownHandler = function (event) {
        if (!activeDialog || !activeDialog.isConnected) {
          onClose();
          return;
        }
        if (event.key === "Tab") {
          var items = visibleFocusables(activeDialog);
          if (items.length === 0) {
            return;
          }
          var first = items[0];
          var last = items[items.length - 1];
          var current = document.activeElement;
          if (!activeDialog.contains(current)) {
            event.preventDefault();
            first.focus();
          } else if (event.shiftKey && current === first) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && current === last) {
            event.preventDefault();
            first.focus();
          }
        } else if (event.key === "Escape") {
          var close = findCloseControl(activeDialog);
          if (close) {
            close.click();
          }
        }
      };
      document.addEventListener("keydown", keydownHandler, true);
    });
  }
  function onClose() {
    teardownTrap();
    var target = triggerElement;
    triggerElement = null;
    activeDialog = null;
    if (!target || !target.isConnected) {
      return;
    }
    // Klaviyo moves focus to its post-close reminder/teaser tab AFTER the close
    // event, so a single focus() gets overridden. Re-assert over a short window,
    // but only while focus is unset, on <body>, or stolen back into Klaviyo — so
    // a user who has deliberately moved elsewhere is never yanked back.
    var attempts = 0;
    (function restore() {
      var active = document.activeElement;
      var focusIsLoose =
        !active || active === document.body || isInsideKlaviyo(active);
      if (focusIsLoose && target.isConnected) {
        try {
          target.focus();
        } catch (error) {
          /* trigger removed from DOM; nothing to restore */
        }
      }
      if (attempts++ < 6) {
        window.setTimeout(restore, 60);
      }
    })();
  }
  window.addEventListener("klaviyoForms", function (event) {
    var detail = event && event.detail;
    if (!detail) {
      return;
    }
    if (detail.type === "open") {
      onOpen(detail.formId);
    } else if (detail.type === "close") {
      onClose();
    }
  });
})();